import { CircularProgress, Link, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import useAuth from 'hooks/useAuth';
import { useBillingClient } from 'client/client-hook';
import { CHECKOUT_COPY } from 'lib/billing';
import { HasPendingCryptoPaymentReply } from 'proto/billing-manager_pb';
import { useCheckoutState } from '../store';
import { overdueCryptoTopUpCents } from '../store/overdue';
import type { OverdueInvoice } from '../store/types';
import { formatCents } from '../format';
import { NotePanel } from '../NotePanel';

const COPY = CHECKOUT_COPY.payment.crypto;
const OVERDUE_COPY = CHECKOUT_COPY.payment.overdue;

// What the crypto CTA does with this balance: settle the invoice from it, or top up only what it leaves.
function cryptoNote(overdue: OverdueInvoice, credit: number): string {
  const money = (cents: number) => formatCents(cents, overdue.currency);
  const due = money(overdue.amountDueCents);
  const rest = money(overdueCryptoTopUpCents(overdue.amountDueCents, credit));
  if (credit >= overdue.amountDueCents) return OVERDUE_COPY.cryptoCovers(money(credit), due);
  if (credit > 0) return OVERDUE_COPY.cryptoPart(money(credit), due, rest);
  if (credit < 0) return OVERDUE_COPY.cryptoOwed(money(-credit), rest);
  return OVERDUE_COPY.cryptoFull(due);
}

// Informational panel for the crypto tab in invoice-pay mode. The actual
// settle action lives on the step's primary CTA (instant from balance when
// credit covers the invoice, otherwise a Coingate redirect). This panel only
// surfaces the wallet balance, the amount due, and any in-flight crypto
// payment the user is returning to.
export function OverdueCryptoPanel() {
  const state = useCheckoutState() as any;
  const overdue = state.overdueInvoice;
  const availableCredit = Number(state.billingOverview?.availableCredit || 0);
  const { user } = useAuth() as any;
  const getBillingClient = useBillingClient();
  const [pending, setPending] = useState<{ loading: boolean; pending: boolean; status: string; purchaseUrl: string }>({
    loading: true,
    pending: false,
    status: '',
    purchaseUrl: ''
  });

  // On return from a Coingate redirect a crypto payment may still be
  // confirming — surface it so the user waits instead of starting another.
  useEffect(() => {
    let cancelled = false;
    if (!user?.tcId) {
      setPending({ loading: false, pending: false, status: '', purchaseUrl: '' });
      return;
    }
    (async () => {
      try {
        const client = await getBillingClient();
        const resp = (await client.hasPendingCryptoPayment({ userId: user.tcId })) as HasPendingCryptoPaymentReply.AsObject;
        if (cancelled) return;
        setPending({
          loading: false,
          pending: Boolean(resp?.hasPendingCryptoPayment),
          status: resp?.status || '',
          purchaseUrl: resp?.purchaseUrl || ''
        });
      } catch {
        if (!cancelled) setPending({ loading: false, pending: false, status: '', purchaseUrl: '' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.tcId, getBillingClient]);

  if (!overdue) return null;

  if (pending.loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          {COPY.checking}
        </Typography>
      </Stack>
    );
  }

  if (pending.pending) {
    return (
      <NotePanel severity="info" title={COPY.pendingTitle} body={COPY.pendingDesc(pending.status)}>
        {pending.purchaseUrl && (
          <Link href={pending.purchaseUrl} target="_blank" rel="noopener" underline="hover" variant="caption" sx={{ pt: 0.5 }}>
            {COPY.openPending}
          </Link>
        )}
      </NotePanel>
    );
  }

  const covers = availableCredit >= overdue.amountDueCents;
  return <NotePanel severity={covers ? 'success' : 'info'} title={COPY.title} body={cryptoNote(overdue, availableCredit)} />;
}
